import { CSSProperties } from 'react'
import {
  MenuPageBuilder,
  isMenuPageBuilderActive,
  sanitizeMenuPageBuilder,
} from 'src/shared/dbTypes/menuPageBuilder'
import config from 'src/config'

export interface ResolvedPageBuilder {
  enabled: boolean
  useAsHomePage: boolean
  layout: {
    stylePreset: 'individual' | 'westwing'
    navigationPosition: 'side' | 'top'
    navigationDisplay: 'none' | 'separate' | 'inline'
    heroMobileMode: 'content' | 'fullscreen'
    contentWidth: 'contained' | 'wide' | 'full'
    magazinePlacement: 'afterHero' | 'afterFilters' | 'beforeProducts' | 'afterProducts' | 'hidden'
    hideLegacyHeader: boolean
  }
  theme: {
    fontFamily?: string
    backgroundColor: string
    surfaceColor: string
    textColor: string
    mutedTextColor: string
    accentColor: string
    accentContrastColor: string
    buttonBackgroundColor: string
    buttonHoverColor: string
    buttonTextColor: string
    purchaseButtonBackgroundColor: string
    purchaseButtonHoverColor: string
    purchaseButtonTextColor: string
    infoColor: string
    successColor: string
    warningColor: string
    errorColor: string
    navigationBackgroundColor: string
    navigationTextColor: string
    navigationOverlayOpacity: number
  }
  search: {
    showCategories: boolean
    showBrands: boolean
    showProducts: boolean
    items: NonNullable<NonNullable<MenuPageBuilder['search']>['items']>
  }
  products: {
    columnsPerRow: number
    columnsPerRowMobile: number
  }
  hero?: NonNullable<MenuPageBuilder['hero']>
  blocks: NonNullable<MenuPageBuilder['blocks']>
}

export interface PageChrome {
  builderEnabled: boolean
  navigationPosition: 'side' | 'top'
  navigationDisplay: 'none' | 'separate' | 'inline'
  hideLegacyHeader: boolean
}

export const resolvePageBuilder = (
  pageBuilder?: MenuPageBuilder
): ResolvedPageBuilder => {
  const safeBuilder = sanitizeMenuPageBuilder(pageBuilder)
  const enabled = isMenuPageBuilderActive(safeBuilder)
  const pageBuilderDefaults = config.pageBuilderDefaults || {}
  const defaultLayout = pageBuilderDefaults.layout || {}
  const defaultTheme = pageBuilderDefaults.theme || {}
  const defaultProducts = pageBuilderDefaults.products || {}

  return {
    enabled,
    useAsHomePage: safeBuilder?.useAsHomePage ?? false,
    layout: {
      stylePreset: safeBuilder?.layout?.stylePreset ?? defaultLayout.stylePreset ?? 'individual',
      navigationPosition: safeBuilder?.layout?.navigationPosition ?? defaultLayout.navigationPosition ?? 'side',
      navigationDisplay: safeBuilder?.layout?.navigationDisplay ?? defaultLayout.navigationDisplay ?? 'separate',
      heroMobileMode: safeBuilder?.layout?.heroMobileMode ?? defaultLayout.heroMobileMode ?? 'fullscreen',
      contentWidth: safeBuilder?.layout?.contentWidth ?? defaultLayout.contentWidth ?? 'contained',
      magazinePlacement: safeBuilder?.layout?.magazinePlacement ?? defaultLayout.magazinePlacement ?? 'afterFilters',
      hideLegacyHeader: safeBuilder?.layout?.hideLegacyHeader ?? defaultLayout.hideLegacyHeader ?? true,
    },
    theme: {
      fontFamily: safeBuilder?.theme?.fontFamily || defaultTheme.fontFamily,
      backgroundColor: safeBuilder?.theme?.backgroundColor || defaultTheme.backgroundColor || '#f6f1ea',
      surfaceColor: safeBuilder?.theme?.surfaceColor || defaultTheme.surfaceColor || '#ffffff',
      textColor: safeBuilder?.theme?.textColor || defaultTheme.textColor || '#1f2933',
      mutedTextColor: safeBuilder?.theme?.mutedTextColor || defaultTheme.mutedTextColor || '#52606d',
      accentColor: safeBuilder?.theme?.accentColor || defaultTheme.accentColor || '#b86f52',
      accentContrastColor: safeBuilder?.theme?.accentContrastColor || defaultTheme.accentContrastColor || '#ffffff',
      buttonBackgroundColor: safeBuilder?.theme?.buttonBackgroundColor || defaultTheme.buttonBackgroundColor || safeBuilder?.theme?.accentColor || defaultTheme.accentColor || '#b86f52',
      buttonHoverColor: safeBuilder?.theme?.buttonHoverColor || defaultTheme.buttonHoverColor || safeBuilder?.theme?.buttonBackgroundColor || defaultTheme.buttonBackgroundColor || safeBuilder?.theme?.accentColor || defaultTheme.accentColor || '#9f5f45',
      buttonTextColor: safeBuilder?.theme?.buttonTextColor || defaultTheme.buttonTextColor || safeBuilder?.theme?.accentContrastColor || defaultTheme.accentContrastColor || '#ffffff',
      purchaseButtonBackgroundColor: safeBuilder?.theme?.purchaseButtonBackgroundColor || defaultTheme.purchaseButtonBackgroundColor || '#1f5b43',
      purchaseButtonHoverColor: safeBuilder?.theme?.purchaseButtonHoverColor || defaultTheme.purchaseButtonHoverColor || '#184734',
      purchaseButtonTextColor: safeBuilder?.theme?.purchaseButtonTextColor || defaultTheme.purchaseButtonTextColor || '#ffffff',
      infoColor: safeBuilder?.theme?.infoColor || defaultTheme.infoColor || '#2563eb',
      successColor: safeBuilder?.theme?.successColor || defaultTheme.successColor || '#168a4a',
      warningColor: safeBuilder?.theme?.warningColor || defaultTheme.warningColor || '#b7791f',
      errorColor: safeBuilder?.theme?.errorColor || defaultTheme.errorColor || '#c2410c',
      navigationBackgroundColor: safeBuilder?.theme?.navigationBackgroundColor || defaultTheme.navigationBackgroundColor || '#ffffff',
      navigationTextColor: safeBuilder?.theme?.navigationTextColor || defaultTheme.navigationTextColor || '#1f2933',
      navigationOverlayOpacity: safeBuilder?.theme?.navigationOverlayOpacity ?? defaultTheme.navigationOverlayOpacity ?? 86,
    },
    search: {
      showCategories: safeBuilder?.search?.showCategories !== false,
      showBrands: safeBuilder?.search?.showBrands !== false,
      showProducts: safeBuilder?.search?.showProducts !== false,
      items: safeBuilder?.search?.items || [],
    },
    products: {
      columnsPerRow: safeBuilder?.products?.columnsPerRow ?? defaultProducts.columnsPerRow ?? 3,
      columnsPerRowMobile: safeBuilder?.products?.columnsPerRowMobile ?? defaultProducts.columnsPerRowMobile ?? 1,
    },
    hero: safeBuilder?.hero,
    blocks: safeBuilder?.blocks || [],
  }
}

export const getPageChrome = (pageBuilder?: MenuPageBuilder): PageChrome => {
  const resolved = resolvePageBuilder(pageBuilder)

  return {
    builderEnabled: resolved.enabled,
    navigationPosition: resolved.layout.navigationPosition,
    navigationDisplay: resolved.layout.navigationDisplay,
    hideLegacyHeader: resolved.layout.hideLegacyHeader,
  }
}

export const getPageChromeCssVars = (pageBuilder?: MenuPageBuilder): CSSProperties => {
  const resolved = resolvePageBuilder(pageBuilder)

  if (!resolved.enabled) return {}

  return {
    ['--page-builder-font-family' as string]: resolved.theme.fontFamily || 'inherit',
    ['--page-builder-background' as string]: resolved.theme.backgroundColor,
    ['--page-builder-surface' as string]: resolved.theme.surfaceColor,
    ['--page-builder-text' as string]: resolved.theme.textColor,
    ['--page-builder-muted' as string]: resolved.theme.mutedTextColor,
    ['--page-builder-accent' as string]: resolved.theme.accentColor,
    ['--page-builder-accent-contrast' as string]: resolved.theme.accentContrastColor,
    ['--page-builder-button-bg' as string]: resolved.theme.buttonBackgroundColor,
    ['--page-builder-button-hover' as string]: resolved.theme.buttonHoverColor,
    ['--page-builder-button-text' as string]: resolved.theme.buttonTextColor,
    ['--page-builder-purchase-button-bg' as string]: resolved.theme.purchaseButtonBackgroundColor,
    ['--page-builder-purchase-button-hover' as string]: resolved.theme.purchaseButtonHoverColor,
    ['--page-builder-purchase-button-text' as string]: resolved.theme.purchaseButtonTextColor,
    ['--page-builder-info' as string]: resolved.theme.infoColor,
    ['--page-builder-success' as string]: resolved.theme.successColor,
    ['--page-builder-warning' as string]: resolved.theme.warningColor,
    ['--page-builder-error' as string]: resolved.theme.errorColor,
    ['--page-builder-nav-bg' as string]: resolved.theme.navigationBackgroundColor,
    ['--page-builder-nav-text' as string]: resolved.theme.navigationTextColor,
    ['--page-builder-nav-overlay-opacity' as string]: String(resolved.theme.navigationOverlayOpacity),
  }
}
